if (!customElements.get('product-details-modal')) {
  class ProductDetailsModal extends HTMLElement {
    constructor() {
      super();
      this.addToBoxCallback = null;
      this.productElement = null;
      this.currentProductUrl = null;
      this.addToBoxChangeHandler = null;
      this.addToBoxVariantUnsubscriber = null;
      this._originalToggleAddButton = null;
      this.buttonObserver = null;
      this.handleOpenEvent = this.handleOpenEvent.bind(this);
      this.handleKeyUp = this.handleKeyUp.bind(this);
    }

    connectedCallback() {
      this.dialogEl = this.querySelector('.product-details-modal__dialog');
      this.contentEl = this.querySelector('.product-details-modal__content');
      this.overlayEl = this.querySelector('.product-details-modal__overlay');
      this.giftIconTpl = this.querySelector('.product-details-modal__gift-icon-tpl');

      this.querySelector('.product-details-modal__close')?.addEventListener('click', () => this.hide());
      this.overlayEl?.addEventListener('click', () => this.hide());
      document.addEventListener('keyup', this.handleKeyUp);
      window.addEventListener('product-details-modal:open', this.handleOpenEvent);
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this.handleKeyUp);
      window.removeEventListener('product-details-modal:open', this.handleOpenEvent);
    }

    handleKeyUp(e) {
      if (e.code === 'Escape' && this.hasAttribute('open')) this.hide();
    }

    handleOpenEvent(e) {
      const { productUrl, addToBoxCallback } = e.detail || {};
      this.addToBoxCallback = addToBoxCallback ?? null;
      this.show(productUrl);
    }

    show(productUrl) {
      this.currentProductUrl = productUrl.split('?')[0];
      this.setAttribute('open', '');
      this.setAttribute('data-loading', '');
      document.body.classList.add('overflow-hidden');

      this.contentEl.innerHTML = `
        <div class="product-details-modal__loading">
          <div class="product-details-modal__skeleton">
            <div class="product-details-modal__skeleton-media">
              <div class="product-details-modal__skeleton-block product-details-modal__skeleton-image"></div>
            </div>
            <div class="product-details-modal__skeleton-info">
              <div class="product-details-modal__skeleton-block product-details-modal__skeleton-rating"></div>
              <div class="product-details-modal__skeleton-block product-details-modal__skeleton-title"></div>
              <div class="product-details-modal__skeleton-block product-details-modal__skeleton-price"></div>
              <div class="product-details-modal__skeleton-block product-details-modal__skeleton-sizes"></div>
              <div class="product-details-modal__skeleton-block product-details-modal__skeleton-button"></div>
            </div>
          </div>
          <div class="product-details-modal__loading-spinner">
            <div class="loading__spinner"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle></svg></div>
          </div>
        </div>`;

      if (typeof trapFocus === 'function') trapFocus(this.dialogEl);

      fetch(productUrl)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          this.productElement = doc.querySelector('section[id^="ProductDetailsModal-"]');
          if (!this.productElement) {
            console.error('[ProductDetailsModal] Section not found in response. Check that templates/product.details-modal.json is deployed. URL:', productUrl);
            this.removeAttribute('data-loading');
            this.contentEl.innerHTML = '';
            return;
          }

          this.removeAttribute('data-loading');
          this.preventDuplicatedIDs();
          this.setInnerHTML(this.contentEl, this.productElement.innerHTML);
          this.preventVariantURLSwitching();
          this.setupSizeGuide();
          this.setupSizePicker();

          if (this.addToBoxCallback) {
            this.setAttribute('data-add-to-box', '');
            this.setupAddToBoxMode();
          } else {
            this.removeAttribute('data-add-to-box');
          }

          if (typeof trapFocus === 'function') trapFocus(this.dialogEl);
        })
        .catch((err) => {
          console.error('[ProductDetailsModal] Failed to load product section:', err);
          this.removeAttribute('data-loading');
          this.contentEl.innerHTML = '';
        });
    }

    createGiftIconEl() {
      const wrapper = document.createElement('span');
      if (this.giftIconTpl?.innerHTML) {
        wrapper.innerHTML = this.giftIconTpl.innerHTML;
      } else {
        wrapper.innerHTML = `<svg class="icon icon-gift" width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M2.59264 0.25C2.00706 0.25 1.55045 0.482444 1.26742 0.815808C0.984399 1.14917 0.870364 1.56288 0.870364 1.9375C0.870364 2.48098 1.11749 2.91719 1.43117 3.22794C1.45372 3.25026 1.47755 3.27117 1.50073 3.29246H0.582576C0.494458 3.29272 0.410023 3.32784 0.347711 3.39014C0.285398 3.45245 0.250271 3.53688 0.25 3.625V6.99999C0.250269 7.08811 0.285395 7.17254 0.347708 7.23485C0.410021 7.29715 0.494457 7.33227 0.582576 7.33253H0.870364V13.4175C0.870637 13.5056 0.905765 13.59 0.968078 13.6523C1.03039 13.7146 1.11482 13.7497 1.20294 13.75H12.9559C12.9999 13.7505 13.0436 13.7423 13.0845 13.7258C13.1253 13.7094 13.1625 13.685 13.1939 13.654C13.2253 13.6231 13.2502 13.5863 13.2673 13.5457C13.2844 13.5051 13.2932 13.4615 13.2934 13.4175V7.33253H13.5762C13.6644 7.33227 13.7488 7.29715 13.8111 7.23485C13.8734 7.17254 13.9085 7.08811 13.9088 6.99999V3.625C13.909 3.54257 13.8787 3.46298 13.8237 3.40157C13.7687 3.34017 13.693 3.3013 13.611 3.29246C13.5994 3.29186 13.5878 3.29186 13.5762 3.29246H12.6581C12.6811 3.27117 12.7049 3.25026 12.7275 3.22794C13.0413 2.91719 13.2934 2.48098 13.2934 1.9375C13.2934 1.56288 13.1743 1.14917 12.8914 0.815808C12.6084 0.482443 12.1517 0.25 11.5662 0.25C10.668 0.25 9.67188 0.791251 8.78167 1.44118C8.08984 1.94638 7.47964 2.51683 7.0794 3.03437C6.67901 2.51683 6.06881 1.94638 5.37698 1.44118C4.48693 0.791249 3.49079 0.25 2.59264 0.25ZM2.59264 0.920036C3.19633 0.920036 4.1534 1.37501 4.98484 1.98217C5.55597 2.39909 6.07469 2.88631 6.42426 3.29246H2.68699C2.40333 3.13537 2.113 2.95967 1.90272 2.75147C1.67512 2.52607 1.53552 2.28882 1.53552 1.9375C1.53552 1.70331 1.61255 1.43746 1.77375 1.24761C1.93464 1.05776 2.17081 0.920036 2.59264 0.920036ZM11.5662 0.920036C11.988 0.920036 12.2239 1.05776 12.3851 1.24761C12.5463 1.43746 12.6233 1.70331 12.6233 1.9375C12.6233 2.28882 12.4886 2.52607 12.261 2.75147C12.0507 2.95967 11.7604 3.13538 11.4767 3.29246H7.73455C8.08396 2.88631 8.60284 2.39909 9.17381 1.98217C10.0054 1.37501 10.9625 0.920036 11.5662 0.920036ZM0.919917 3.95753H2.56279C2.58423 3.95961 2.60582 3.95961 2.62727 3.95753H6.74683V6.66746H0.919917V3.95753ZM7.41198 3.95753H11.4718C11.5256 3.97114 11.5818 3.97114 11.6356 3.95753H13.2437V6.66746H7.41198V3.95753ZM1.53552 7.33253H6.74683V13.0849H1.53552V7.33253ZM7.41198 7.33253H12.6233V13.0849H7.41198V7.33253Z" fill="currentColor" stroke="currentColor" stroke-width="0.5"/></svg>`;
      }
      return wrapper;
    }

    setupAddToBoxMode() {
      const form = this.contentEl.querySelector('[data-type="add-to-cart-form"]');
      if (!form) return;

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.setAttribute('data-add-to-box-mode', '');
        const span = submitBtn.querySelector('span');
        if (span) {
          const priceEl = this.contentEl.querySelector('.product__price .price__regular .price-item--regular');
          const priceText = priceEl ? priceEl.textContent.trim() + ' - ' : '';
          span.textContent = `${priceText}${window.productDetailsModalStrings?.addToBox || 'Add to box'}`;
          span.appendChild(this.createGiftIconEl());

          if (this.buttonObserver) this.buttonObserver.disconnect();
          this.buttonObserver = new MutationObserver((_mutations, observer) => {
            if (!form.isConnected) return;
            observer.disconnect();
            const btn = form.querySelector('[type="submit"]');
            if (btn) {
              const variantInput = form.querySelector('[name="id"]');
              const variantId = parseInt(variantInput?.value);
              const isAvailable = variantId ? this.isVariantAvailable(variantId) : true;
              if (isAvailable) {
                this.setAddToBoxButtonText(btn, this.getAddToBoxButtonText(), true);
                btn.removeAttribute('disabled');
              } else {
                this.setAddToBoxButtonText(btn, window.variantStrings?.soldOut || 'Sold out', false);
              }
            }
            observer.observe(submitBtn, { childList: true, subtree: true });
          });
          this.buttonObserver.observe(submitBtn, { childList: true, subtree: true });
        }
      }

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        const variantInput = form.querySelector('[name="id"]');
        const variantId = parseInt(variantInput?.value);
        if (!variantId || !this.addToBoxCallback) return;
        if (!this.isVariantAvailable(variantId)) {
          this.syncAddToBoxButtonState(form);
          return;
        }

        const personalisation = this.getPersonalisationData(form);
        const accordionEl = form.querySelector('personalisation-accordion');
        const monogramVariantId = accordionEl?.dataset.monogramProduct
          ? parseInt(accordionEl.dataset.monogramProduct)
          : null;

        this.addToBoxCallback({ variantId, personalisation, monogramVariantId });
        this.hide();
      }, true);

      this.syncAddToBoxButtonState(form);
      if (this.addToBoxChangeHandler) {
        this.contentEl.removeEventListener('change', this.addToBoxChangeHandler);
      }
      this.addToBoxChangeHandler = () => {
        if (!form.isConnected) return;
        this.syncAddToBoxButtonState(form);
      };
      this.contentEl.addEventListener('change', this.addToBoxChangeHandler);

      if (this.addToBoxVariantUnsubscriber) this.addToBoxVariantUnsubscriber();
      const variantSelects = this.contentEl.querySelector('variant-selects');
      const sectionId = variantSelects?.dataset.originalSection;

      // Patch VariantSelects prototype so toggleAddButton never overwrites "Add to box" text.
      const VariantSelectsClass = customElements.get('variant-selects');
      if (VariantSelectsClass && typeof VariantSelectsClass.prototype.toggleAddButton === 'function' && !this._originalToggleAddButton) {
        const originalMethod = VariantSelectsClass.prototype.toggleAddButton;
        this._originalToggleAddButton = originalMethod;
        VariantSelectsClass.prototype.toggleAddButton = function(disable, text, modifyClass) {
          const modal = this.closest('product-details-modal[data-add-to-box]');
          if (modal) {
            const form = modal.querySelector('[data-type="add-to-cart-form"]');
            const addButton = form?.querySelector('[name="add"]');
            if (addButton?.hasAttribute('data-add-to-box-mode')) {
              if (disable) addButton.setAttribute('disabled', 'disabled');
              else addButton.removeAttribute('disabled');
              return;
            }
          }
          return originalMethod.call(this, disable, text, modifyClass);
        };
      }

      if (!sectionId) {
        console.warn('[ProductDetailsModal] Could not determine sectionId from variant-selects — button state may not restore after variant change.');
      } else if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.addToBoxVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== sectionId) return;
          if (!form.isConnected) return;
          this.syncAddToBoxButtonState(form);
        });
      }
    }

    getVariantData() {
      const variantsJson = this.contentEl.querySelector('variant-selects script[type="application/json"]');
      if (!variantsJson) return [];

      try {
        return JSON.parse(variantsJson.textContent || '[]');
      } catch (e) {
        console.error('[ProductDetailsModal] Failed to parse variant data:', e);
        return [];
      }
    }

    isVariantAvailable(variantId) {
      const variant = this.getVariantData().find((item) => Number(item.id) === Number(variantId));
      return variant ? Boolean(variant.available) : true;
    }

    getAddToBoxButtonText() {
      const initialsInput = this.contentEl.querySelector('.personalisation-property[name="properties[Initials]"]:not([disabled])');
      const isPersonalised = Boolean(initialsInput?.value);

      let priceText = '';
      if (isPersonalised) {
        const sectionId = this.contentEl.querySelector('variant-selects')?.dataset.section;
        if (sectionId) {
          const personalisedPriceEl = this.contentEl.querySelector(`.variantPrice-${sectionId}-personalised`);
          const personalisedText = personalisedPriceEl?.textContent?.trim();
          if (personalisedText) priceText = `${personalisedText} - `;
        }
      }

      if (!priceText) {
        const priceEl = this.contentEl.querySelector('.product__price .price__regular .price-item--regular');
        priceText = priceEl ? `${priceEl.textContent.trim()} - ` : '';
      }

      return `${priceText}${window.productDetailsModalStrings?.addToBox || 'Add to box'}`;
    }

    setAddToBoxButtonText(submitBtn, text, showIcon = true) {
      const span = submitBtn.querySelector('span');
      if (!span) return;

      let textNode = Array.from(span.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
      if (!textNode) {
        textNode = document.createTextNode('');
        span.insertBefore(textNode, span.firstChild);
      }

      textNode.textContent = text;

      let giftIconWrapper = span.querySelector('.icon-gift')?.closest('span');
      if (!giftIconWrapper) {
        giftIconWrapper = this.createGiftIconEl();
        span.appendChild(giftIconWrapper);
      }
      giftIconWrapper.hidden = !showIcon;
    }

    syncAddToBoxButtonState(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      if (!submitBtn) return;

      const variantInput = form.querySelector('[name="id"]');
      const variantId = parseInt(variantInput?.value);
      const isAvailable = variantId ? this.isVariantAvailable(variantId) : true;

      if (isAvailable) {
        submitBtn.removeAttribute('disabled');
        this.setAddToBoxButtonText(submitBtn, this.getAddToBoxButtonText(), true);
      } else {
        submitBtn.setAttribute('disabled', 'disabled');
        this.setAddToBoxButtonText(submitBtn, window.variantStrings?.soldOut || 'Sold out', false);
      }
    }

    getPersonalisationData(form) {
      const initialsInput = form.querySelector('.personalisation-property[name="properties[Initials]"]:not([disabled])');
      if (!initialsInput?.value) return null;
      const fontInput = form.querySelector('.personalisation-property[name="properties[Font]"]:not([disabled])');
      const colorInput = form.querySelector('.personalisation-property[name="properties[Color]"]:not([disabled])');
      return {
        initials: initialsInput.value,
        font: fontInput?.value || '',
        color: colorInput?.value || '',
      };
    }

    hide() {
      this.removeAttribute('open');
      this.removeAttribute('data-add-to-box');
      document.body.classList.remove('overflow-hidden');

      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
        this.buttonObserver = null;
      }
      if (this.addToBoxChangeHandler) {
        this.contentEl.removeEventListener('change', this.addToBoxChangeHandler);
        this.addToBoxChangeHandler = null;
      }
      if (this.addToBoxVariantUnsubscriber) {
        this.addToBoxVariantUnsubscriber();
        this.addToBoxVariantUnsubscriber = null;
      }
      if (this._originalToggleAddButton) {
        const VariantSelectsClass = customElements.get('variant-selects');
        if (VariantSelectsClass) {
          VariantSelectsClass.prototype.toggleAddButton = this._originalToggleAddButton;
        }
        this._originalToggleAddButton = null;
      }
      this.contentEl.innerHTML = '';
      this.addToBoxCallback = null;
      this.productElement = null;
      this.currentProductUrl = null;
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(
        sectionId,
        `pdm-${sectionId}`
      );
      this.productElement.querySelectorAll('variant-selects, product-info').forEach((el) => {
        el.dataset.originalSection = sectionId;
      });
    }

    setupSizePicker() {
      this.contentEl.querySelectorAll('.size-picker a[href]').forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const href = link.getAttribute('href');
          this.show(href + '?view=details-modal');
        });
      });
    }

    setupSizeGuide() {
      const infoCol = this.contentEl.querySelector('.product-details-modal__info-col');
      if (!infoCol) return;
      const trigger = this.contentEl.querySelector('.size-guide-trigger');
      if (!trigger) return;

      this.contentEl.querySelectorAll('.product-form__fieldset--size').forEach((el) => {
        el.classList.add('is-active');
      });

      const panel = document.createElement('div');
      panel.className = 'product-details-modal__size-guide-panel';
      infoCol.appendChild(panel);

      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'product-details-modal__size-guide-back link--underline-hover body-3';
      backBtn.innerHTML = `&#8249; ${window.productDetailsModalStrings?.backToProduct || 'back to product'}`;
      backBtn.addEventListener('click', () => panel.removeAttribute('data-open'));

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        if (!panel.dataset.loaded) {
          panel.dataset.loaded = 'true';
          panel.appendChild(backBtn);

          const spinner = document.createElement('div');
          spinner.className = 'product-details-modal__size-guide-loading';
          spinner.innerHTML = '<div class="loading__spinner"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle></svg></div>';
          panel.appendChild(spinner);

          const globalSizeGuide = document.querySelector('size-guide-modal');
          if (globalSizeGuide) {
            spinner.remove();
            this.populateSizeGuidePanel(panel, globalSizeGuide);
          } else if (this.currentProductUrl) {
            fetch(this.currentProductUrl)
              .then((r) => r.text())
              .then((html) => {
                spinner.remove();
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const sizeGuide = doc.querySelector('size-guide-modal');
                if (sizeGuide) this.populateSizeGuidePanel(panel, sizeGuide);
              })
              .catch(() => { spinner.remove(); });
          }
        }

        panel.setAttribute('data-open', '');
      });
    }

    populateSizeGuidePanel(panel, sizeGuide) {
      const titleEl = sizeGuide.querySelector('.size-guide__title');
      if (titleEl) {
        const heading = document.createElement('h2');
        heading.className = 'product-details-modal__size-guide-title h-l';
        heading.textContent = titleEl.textContent.trim();
        panel.appendChild(heading);
      }

      const tabbedInfo = sizeGuide.querySelector('tabbed-info');
      if (!tabbedInfo) return;

      const inner = document.createElement('div');
      inner.className = 'product-details-modal__size-guide-inner';
      inner.innerHTML = tabbedInfo.outerHTML;
      panel.appendChild(inner);

      const tabPanels = Array.from(inner.querySelectorAll('.tabbed-info__tabpanel'));
      const tabButtons = Array.from(inner.querySelectorAll('.tabbed-info__tab-button'));

      tabPanels.forEach((p, i) => { p.hidden = i !== 0; });
      tabButtons.forEach((btn, i) => {
        if (i === 0) {
          btn.setAttribute('aria-selected', 'true');
          btn.classList.remove('button--secondary');
          btn.classList.add('button--primary');
        }
        btn.addEventListener('click', () => {
          const panelId = btn.getAttribute('aria-controls');
          tabButtons.forEach((b) => {
            b.setAttribute('aria-selected', 'false');
            b.classList.remove('button--primary');
            b.classList.add('button--secondary');
          });
          tabPanels.forEach((p) => p.setAttribute('hidden', ''));
          btn.setAttribute('aria-selected', 'true');
          btn.classList.remove('button--secondary');
          btn.classList.add('button--primary');
          const target = inner.querySelector(`#${panelId}`);
          if (target) target.removeAttribute('hidden');
        });
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.contentEl.querySelector('variant-selects');
      if (!variantPicker) return;
      variantPicker.setAttribute('data-update-url', 'false');
      const url = variantPicker.dataset.url || '';
      if (!url.includes('view=details-modal')) {
        variantPicker.dataset.url = url + (url.includes('?') ? '&' : '?') + 'view=details-modal';
      }
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach((oldTag) => {
        const newTag = document.createElement('script');
        Array.from(oldTag.attributes).forEach((attr) => newTag.setAttribute(attr.name, attr.value));
        newTag.appendChild(document.createTextNode(oldTag.innerHTML));
        oldTag.parentNode.replaceChild(newTag, oldTag);
      });
    }
  }

  customElements.define('product-details-modal', ProductDetailsModal);
}

if (!customElements.get('product-details-modal-opener')) {
  class ProductDetailsModalOpener extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', (e) => {
        e.preventDefault();
        window.dispatchEvent(
          new CustomEvent('product-details-modal:open', {
            detail: { productUrl: this.dataset.productUrl },
          })
        );
      });
    }
  }

  customElements.define('product-details-modal-opener', ProductDetailsModalOpener);
}
